import { datadogRum } from "@datadog/browser-rum";
import type { AnalyticsProvider } from "../common/types";

interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  site?: string;
  service?: string;
  env?: string;
  version?: string; // app version or commit sha
  sampleRate?: number;
  allowedTracingUrls?: Array<string | RegExp>;
  traceSampleRate?: number;
}

export class DatadogProvider implements AnalyticsProvider {
  private initialized = false;

  init(config: DatadogConfig): void {
    if (this.initialized) return;

    // Prefer explicit version, then common CI envs exposed to the client. Vercel
    // populates NEXT_PUBLIC_APP_VERSION with the literal string
    // "_vercel_git_commit_sha" when no value is injected. Treat that as
    // undefined so we can fall back to other sources.
    const appVersion = process.env.NEXT_PUBLIC_APP_VERSION;
    const resolvedVersion =
      config.version ??
      (appVersion && appVersion !== "_vercel_git_commit_sha" ? appVersion : undefined) ??
      (process.env.VERCEL_GIT_COMMIT_SHA as string | undefined) ??
      (process.env.GITHUB_SHA as string | undefined);

    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site ?? "datadoghq.com",
      service: config.service,
      env: config.env,
      version: resolvedVersion,
      sessionSampleRate: config.sampleRate ?? 100,
      traceSampleRate: config.traceSampleRate ?? 100,
      trackUserInteractions: true,
      trackResources: true,
      allowedTracingUrls: config.allowedTracingUrls,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: "mask-user-input",
    });
    this.initialized = true;
  }

  identify(user: { id: string } & Record<string, unknown>): void {
    if (!this.initialized) return;
    datadogRum.setUser(user);
  }

  track(event: string, props?: Record<string, unknown>): void {
    if (!this.initialized) return;
    datadogRum.addAction(event, props);
  }

  trackError(
    event: string,
    props?: Record<string, unknown>,
    level: "error" | "warn" = "error",
  ): void {
    if (!this.initialized) return;
    const error = new Error(event);
    error.name = level === "warn" ? "Warning" : "Error";

    // Add level information to the properties for better categorization
    const enhancedProps = {
      ...props,
      level,
      status_code: level === "warn" ? "Warning" : "Error",
      status_description: event,
    };

    datadogRum.addError(error, enhancedProps);
  }

  page(name: string): void {
    if (!this.initialized) return;
    datadogRum.startView(name);
  }

  shutdown(): void {
    // datadog rum does not expose a shutdown; stop session replay to reduce noise
    if (!this.initialized) return;
    datadogRum.stopSession();
  }
}
